
import { Helmet } from "react-helmet-async";
import { getCanonicalUrl, config } from "@/utils/config";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
}

const SEO = ({ 
  title = "PsyOnline - Atendimento Psicológico Online",
  description = "Plataforma de atendimento psicológico online. Agende consultas, conecte-se via Google Meet e faça pagamentos com segurança.",
  keywords = "psicologia online, terapia, consulta psicológica, saúde mental, psicólogo online",
  image = "/opengraph-image.png",
  url = "",
  type = "website",
  noIndex = false
}: SEOProps) => {
  const canonicalUrl = getCanonicalUrl(url);
  const fullImageUrl = image.startsWith('http') ? image : `${config.app.siteUrl}${image}`;
  const fullTitle = title.includes('PsyOnline') ? title : `${title} | PsyOnline`;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="PsyOnline" />
      <meta property="og:locale" content="pt_BR" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@psyonline" />
      
      {/* Additional meta */}
      <meta name="author" content="PsyOnline" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "PsyOnline",
          "url": config.app.siteUrl,
          "logo": `${config.app.siteUrl}/logo.png`,
          "description": description,
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "BR"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": "Portuguese"
          },
          "sameAs": [
            "https://twitter.com/psyonline",
            "https://facebook.com/psyonline",
            "https://instagram.com/psyonline"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
